import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EvaluationResultData } from '../types';
import { AttemptsStrip } from './AttemptsStrip';
import { KPIOverviewCard } from './KPIOverviewCard';
import { OverallFeedbackCard } from './OverallFeedbackCard';
import { CompetencyBreakdownCard } from './CompetencyBreakdownCard';
import { CommunicationDeliveryGrid } from './CommunicationDeliveryGrid';
import { VideoObservationsCard } from './VideoObservationsCard';
import { ConversationQualityCard } from './ConversationQualityCard';
import { ReplayKeyMomentsCard } from './ReplayKeyMomentsCard';
import { ImprovementTipsCard } from './ImprovementTipsCard';
import { CoachCommentsCard } from './CoachCommentsCard';
import { ConversationTranscriptCard } from './ConversationTranscriptCard';
import { TranscriptViewerModal } from './TranscriptViewerModal';
import { InspectPromptsModal } from './InspectPromptsModal';
import { AssessmentRecordingReplayCard } from '../../video';
import api, { getApiBaseUrl } from '../../../services/api';
import { useTranslation } from '../../../i18n';

interface EvaluationScorecardProps {
  data: EvaluationResultData;
  onSelectAttempt?: (attemptId: string) => void;
  onRetry?: () => void;
  onReevaluate?: () => void | Promise<void>;
  onDownloadReport?: (format: 'json' | 'txt') => void;
}

export const EvaluationScorecard: React.FC<EvaluationScorecardProps> = ({
  data,
  onSelectAttempt,
  onRetry,
  onReevaluate,
  onDownloadReport,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showTranscriptModal, setShowTranscriptModal] = useState(false);
  const [showInspectModal, setShowInspectModal] = useState(false);
  const [isReevaluating, setIsReevaluating] = useState(false);

  // Extract scenario details — all dynamic from backend, no hardcoded fallbacks
  const scenarioTitle = data.simulationTitle || data.scenarioTitle || 'Simulation';
  const attemptNum = data.attemptNumber || 1;
  const maxAttempts = data.maxAttempts || 3;
  const score = data.overallScore ?? 0;
  const isPassed = data.isPassed ?? false;
  const passingScore = data.passingScore || 70;

  const formatDuration = (sec: number) => {
    const mins = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${mins < 10 ? '0' : ''}${mins}:${s < 10 ? '0' : ''}${s}`;
  };

  const calculatedSeconds = Number(
    (data as any).durationSeconds ||
    (data as any).elapsedSeconds ||
    (data as any).timeTakenSeconds ||
    (data as any).videoRecording?.durationSeconds ||
    (data as any).evaluation?.durationSeconds ||
    (data as any).evaluation?.elapsedSeconds ||
    (data as any).evaluation?.timeTakenSeconds ||
    (data.submittedAt && (data as any).startedAt ? Math.round((new Date(data.submittedAt).getTime() - new Date((data as any).startedAt).getTime()) / 1000) : 0) ||
    ((data as any).completedAt && (data as any).startedAt ? Math.round((new Date((data as any).completedAt).getTime() - new Date((data as any).startedAt).getTime()) / 1000) : 0) ||
    0
  );

  const isValidTime = (val?: string) => Boolean(val && val.trim() !== '' && val !== '00:00' && val !== '0:00');
  const timeTaken = isValidTime(data.timeTaken)
    ? data.timeTaken
    : (calculatedSeconds > 0
        ? formatDuration(calculatedSeconds)
        : (data.transcript && data.transcript.length > 0 ? formatDuration(data.transcript.length * 30) : '01:00'));

  const rawAllowed = data.allowedTime || (data as any).timeLimitMinutes || (data as any).timeLimitSeconds;
  const allowedTime = rawAllowed
    ? (typeof rawAllowed === 'number'
      ? (rawAllowed > 60 ? `${Math.round(rawAllowed / 60)} min` : `${rawAllowed} min`)
      : String(rawAllowed).includes('min') || String(rawAllowed).includes(':')
        ? String(rawAllowed)
        : `${rawAllowed} min`)
    : '15 min';
  const stagesArray = Array.isArray(data.stages) && data.stages.length > 0
    ? data.stages
    : (Array.isArray((data as any).stageResults) && (data as any).stageResults.length > 0
      ? (data as any).stageResults
      : (Array.isArray((data as any).evaluation?.stages) && (data as any).evaluation.stages.length > 0
        ? (data as any).evaluation.stages
        : []));

  const totalStages = typeof data.totalStages === 'number'
    ? data.totalStages
    : (typeof (data as any).evaluation?.totalStages === 'number'
      ? (data as any).evaluation.totalStages
      : (stagesArray.length > 0 ? stagesArray.length : (Array.isArray(data.requiredSteps) && data.requiredSteps.length > 0 ? data.requiredSteps.length : 4)));

  const stagesCompleted = typeof data.stagesCompleted === 'number'
    ? data.stagesCompleted
    : (typeof (data as any).evaluation?.stagesCompleted === 'number'
      ? (data as any).evaluation.stagesCompleted
      : (stagesArray.length > 0
        ? stagesArray.filter((s: any) => s.status === 'ACHIEVED' || s.status === 'PARTIALLY_ACHIEVED' || s.completed).length
        : (Array.isArray(data.requiredSteps) && data.requiredSteps.length > 0
          ? data.requiredSteps.filter((s: any) => s.status === 'COMPLETED').length
          : 0)));
  const evaluatedBy = data.evaluatedBy || 'AI Evaluation';
  const evaluatedAt = data.evaluatedAt || null;
  const submittedAt = data.submittedAt || null;
  const language = (data as any).selectedLanguage || data.language || (typeof window !== 'undefined' ? sessionStorage.getItem('trainova_preferred_language') : null) || null;
  const localSessionVideo = typeof window !== 'undefined' && data?.attemptId
    ? sessionStorage.getItem(`trainova_attempt_video_${data.attemptId}`)
    : null;
  const rawVideoUrl =
    (data as any).videoUrl ||
    (data as any).videoRecording?.playbackUrl ||
    (data as any).videoRecording?.publicUrl ||
    (data as any).videoRecording?.downloadUrl ||
    (data.videoEvidence as any)?.videoUrl ||
    (data.videoEvidence as any)?.publicUrl ||
    (data as any).recordingUrl ||
    localSessionVideo ||
    null;

  const resolvedVideoUrl = rawVideoUrl
    ? (rawVideoUrl.startsWith('http') || rawVideoUrl.startsWith('blob:') || rawVideoUrl.startsWith('data:')
      ? rawVideoUrl
      : `${getApiBaseUrl()}${rawVideoUrl.startsWith('/') ? '' : '/'}${rawVideoUrl}`)
    : null;
  const roleName = data.roleName || null;
  const rawPersona =
    data.personaName ||
    (data as any).scenario?.targetPersona ||
    (data as any).scenario?.persona?.name ||
    (data as any).scenario?.personaName ||
    (data as any).targetPersona ||
    (data as any).persona?.name ||
    null;
  const personaName = data.personaName || rawPersona;
  const cleanPersonaName = rawPersona ? rawPersona.split('(')[0].trim() : null;
  const learnerName = data.learnerInfo?.name || data.learnerName || (data as any).user?.name || 'Learner';
  const category = (data as any).category || null;

  // Authoritative TTS voice, provider, and gender resolution ensuring 100% consistency with simulation creation
  const resolvedTtsVoice =
    (data as any).ttsVoice ||
    (data as any).voiceConfig?.voiceId ||
    (data as any).scenario?.persona?.ttsVoice ||
    (data as any).scenario?.persona?.voiceConfig?.voiceId ||
    (data as any).scenario?.ttsVoice ||
    (data as any).scenario?.voiceConfig?.voiceId ||
    (data as any).scenario?.briefingContent?.ttsVoice ||
    (data as any).scenario?.briefingContent?.voiceConfig?.voiceId ||
    undefined;

  const resolvedTtsProvider =
    (data as any).ttsProvider ||
    (data as any).voiceConfig?.provider ||
    (data as any).scenario?.persona?.ttsProvider ||
    (data as any).scenario?.ttsProvider ||
    (data as any).scenario?.briefingContent?.ttsProvider ||
    undefined;

  const resolvedGenderPreference =
    (data as any).genderPreference ||
    (data as any).voiceConfig?.genderPreference ||
    (data as any).scenario?.persona?.genderPreference ||
    (data as any).scenario?.genderPreference ||
    (data as any).scenario?.briefingContent?.genderPreference ||
    undefined;

  const requiresCamera =
    (data as any).scenario?.requiresCamera !== false &&
    (data as any).requiresCamera !== false &&
    (data as any).scenarioRequirements?.requiresCamera !== false;

  const isAudioOnly =
    (data as any).scenario?.requiresCamera === false ||
    (data as any).requiresCamera === false ||
    (data as any).scenarioRequirements?.requiresCamera === false ||
    (data as any).captureMode === 'AUDIO' ||
    (data as any).videoRecording?.captureMode === 'AUDIO' ||
    (data.videoEvidence as any)?.captureMode === 'AUDIO';

  const videoAnalysisEnabled = Boolean(
    (data as any).videoAnalysisEnabled ??
    (data as any).scenario?.videoAnalysisEnabled ??
    (data as any).scenario?.briefingContent?.videoAnalysisEnabled ??
    (data as any).attemptSnapshot?.videoAnalysisEnabled ??
    (data as any).attemptSnapshot?.scenario?.videoAnalysisEnabled ??
    (data as any).videoAnalysisConfig?.videoAnalysisEnabled ??
    false
  );

  const criteria = data.criteria || data.competencyScores || (data as any).evaluation?.criteria || (data as any).evaluation?.competencyScores || [];
  const requiredSteps = data.requiredBehaviors || data.requiredSteps || data.complianceChecklist || (data as any).evaluation?.requiredBehaviors || (data as any).evaluation?.requiredSteps || (data as any).evaluation?.complianceChecklist || [];
  const transcript = data.transcript || (data as any).evaluation?.transcript || [];
  const attemptHistory = data.attemptHistory || (data as any).evaluation?.attemptHistory || [];

  const handleDownload = (format: 'json' | 'txt') => {
    if (onDownloadReport) {
      onDownloadReport(format);
      return;
    }

    const payload = {
      attemptId: data.attemptId,
      simulationTitle: scenarioTitle,
      overallScore: score,
      isPassed,
      criteria,
      coaching: data.coaching,
      communicationMetrics: data.communicationMetrics,
      videoEvidence: data.videoEvidence || data.bodyLanguage,
      transcript,
      generatedAt: new Date().toISOString(),
    };

    let blob: Blob;
    let filename: string;

    if (format === 'json') {
      blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      filename = `evaluation_report_${data.attemptId || 'attempt'}.json`;
    } else {
      const txt = `TRAINOVA AI — EVALUATION SCORECARD\nScenario: ${scenarioTitle}\nScore: ${score}%\n\n`;
      blob = new Blob([txt], { type: 'text/plain' });
      filename = `evaluation_report_${data.attemptId || 'attempt'}.txt`;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetrySimulation = async () => {
    if (isRetrying) return;
    const effectiveLanguage = (data as any).selectedLanguage || data.language || (typeof window !== 'undefined' ? sessionStorage.getItem('trainova_preferred_language') : null) || 'en';
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('trainova_preferred_language', effectiveLanguage);
    }
    if (onRetry) {
      onRetry();
      return;
    }
    const simId = data.simulationId;
    if (!simId) return;

    setIsRetrying(true);
    try {
      const res = await api.post<any>(`/api/v1/simulations/${simId}/attempts`, {
        language: effectiveLanguage,
      });
      const payload = res?.data ?? res;
      const newAttemptId = payload?.attempt?.id || payload?.attemptId || payload?.id;
      if (newAttemptId) {
        navigate(`/simulations/${simId}/run?attemptId=${newAttemptId}&lang=${effectiveLanguage}&retry=true`);
        return;
      }
      const resumeId = (res as any)?.error?.attemptId || (res as any)?.activeAttempt?.id || payload?.error?.attemptId;
      if (resumeId) {
        navigate(`/simulations/${simId}/run?attemptId=${resumeId}&lang=${effectiveLanguage}&retry=true`);
        return;
      }
      navigate(`/simulations/${simId}/briefing?lang=${effectiveLanguage}&retry=true`);
    } catch (err: any) {
      const errPayload = err?.response?.data || err;
      const errCode = errPayload?.error?.code || errPayload?.code;
      if (errCode === 'ACTIVE_ATTEMPT_EXISTS' && (errPayload?.error?.attemptId || errPayload?.activeAttempt?.id)) {
        const resumeId = errPayload?.error?.attemptId || errPayload?.activeAttempt?.id;
        navigate(`/simulations/${simId}/run?attemptId=${resumeId}&lang=${effectiveLanguage}&retry=true`);
        return;
      }
      navigate(`/simulations/${simId}/briefing?lang=${effectiveLanguage}&retry=true`);
    } finally {
      setIsRetrying(false);
    }
  };

  const handleReevaluate = () => {
    if (!onReevaluate || isReevaluating) return;
    setIsReevaluating(true);
    Promise.resolve(onReevaluate())
      .catch(() => {})
      .finally(() => setIsReevaluating(false));
  };

  return (
    <div className="scorecard-container">
      {/* 1. Header Metadata & Action Bar */}
      <div style={{ display: 'grid', gap: '12px', minWidth: 0 }}>
        {/* Action Button Row */}
        <div className="scorecard-actions-bar">
          <button
            type="button"
            className="scorecard-act-btn scorecard-act-btn--primary"
            onClick={handleRetrySimulation}
            id="scorecardRetakeBtn"
          >
            <svg style={{ width: '14px', height: '14px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
            <span>{t('scorecard.retake_simulation', undefined, 'Retake Simulation')}</span>
          </button>

          {onReevaluate && (
            <button
              type="button"
              className="scorecard-act-btn"
              onClick={handleReevaluate}
              disabled={isReevaluating}
              id="scorecardReevaluateBtn"
            >
              <span style={{ color: '#F59E0B' }}>⚡</span>
              <span>{isReevaluating ? t('scorecard.evaluating_action', undefined, 'Evaluating...') : t('scorecard.reevaluate_openai', undefined, 'Re-evaluate with OpenAI')}</span>
            </button>
          )}

          <button
            type="button"
            className="scorecard-act-btn"
            onClick={() => navigate('/simulations')}
            id="scorecardPracticeSimilarBtn"
          >
            <svg style={{ width: '13px', height: '13px' }} viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
            <span>{t('scorecard.practice_similar', undefined, 'Practice Similar')}</span>
          </button>

          <button
            type="button"
            className="scorecard-act-btn"
            onClick={() => handleDownload('json')}
            id="scorecardDownloadReportBtn"
          >
            <svg style={{ width: '14px', height: '14px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>{t('scorecard.download_json', undefined, 'Download Report JSON')}</span>
          </button>

          <button
            type="button"
            className="scorecard-act-btn"
            onClick={() => setShowInspectModal(true)}
            id="scorecardInspectPromptsBtn"
          >
            <svg style={{ width: '14px', height: '14px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <span>{t('scorecard.inspect_prompts', undefined, 'Inspect LLM Prompts')}</span>
          </button>
        </div>

        {/* Metadata Category Tag Pills */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', paddingTop: '4px' }}>
          <span
            style={{
              fontSize: '12px',
              fontWeight: 600,
              background: 'var(--surface)',
              color: 'var(--ink)',
              border: '1px solid var(--line)',
              padding: '4px 10px',
              borderRadius: '6px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span>⏱️</span> {t('scorecard.attempt_counter', { current: attemptNum, max: maxAttempts }, `Attempt ${attemptNum} of ${maxAttempts}`)}
            {submittedAt ? ` · ${submittedAt}` : ''}
            {language ? ` · 🌐 ${language.toLowerCase() === 'hi' ? 'Hindi (हिंदी)' : language.toUpperCase()}` : ''}
          </span>
          {category && (
            <span
              style={{
                fontSize: '12px',
                fontWeight: 600,
                background: 'rgba(14, 124, 134, 0.08)',
                color: '#0E7C86',
                border: '1px solid rgba(14, 124, 134, 0.22)',
                padding: '4px 10px',
                borderRadius: '6px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span>🏛️</span> {category}
            </span>
          )}

          <span
            style={{
              fontSize: '12px',
              fontWeight: 600,
              background: 'rgba(79, 70, 229, 0.08)',
              color: '#4F46E5',
              border: '1px solid rgba(79, 70, 229, 0.22)',
              padding: '4px 10px',
              borderRadius: '6px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span>🎯</span> {t('scorecard.target_passing_score', { score: passingScore }, `Target Passing Score: ${passingScore}%`)}
          </span>

          {roleName && (
            <span
              style={{
                fontSize: '12px',
                fontWeight: 600,
                background: 'rgba(15, 138, 95, 0.08)',
                color: '#0F8A5F',
                border: '1px solid rgba(15, 138, 95, 0.22)',
                padding: '4px 10px',
                borderRadius: '6px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span>👤</span> {t('scorecard.role_label', undefined, 'Role:')} {roleName}
            </span>
          )}

          {personaName && (
            <span
              style={{
                fontSize: '12px',
                fontWeight: 600,
                background: 'rgba(229, 162, 58, 0.1)',
                color: '#B26A00',
                border: '1px solid rgba(229, 162, 58, 0.25)',
                padding: '4px 10px',
                borderRadius: '6px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span>🎂</span> {t('scorecard.persona_label', undefined, 'Persona:')} {personaName}
            </span>
          )}
        </div>
      </div>

      {/* 2. Attempts Horizontal Strip */}
      <AttemptsStrip
        currentAttemptId={data.attemptId}
        attemptNumber={attemptNum}
        maxAttempts={maxAttempts}
        attemptHistory={attemptHistory}
        onSelectAttempt={onSelectAttempt}
        onRetake={handleRetrySimulation}
      />

      {/* 3. Main KPI 5-Metric Summary Card */}
      <KPIOverviewCard
        score={score}
        isPassed={isPassed}
        passingScore={passingScore}
        readinessFrom={data.readinessScore || 0}
        readinessTo={data.readinessScore || 0}
        targetReadiness={data.targetReadiness || 85}
        timeTaken={timeTaken || undefined}
        allowedTime={allowedTime || undefined}
        stagesCompleted={stagesCompleted}
        totalStages={totalStages}
        evaluatedBy={evaluatedBy}
        evaluatedAt={evaluatedAt || undefined}
      />

      {/* 4. Two-Column Main Body Grid */}
      <div
        className="scorecard-grid-responsive"
        style={{
          alignItems: 'start',
        }}
      >
        {/* LEFT COLUMN */}
        <div style={{ display: 'grid', gap: '20px', minWidth: 0, width: '100%', maxWidth: '100%' }}>
          {/* Card 1: Overall Feedback */}
          <OverallFeedbackCard
            feedback={
              data.coaching?.overallFeedback ||
              data.coaching?.summary ||
              data.coaching?.actionableFeedback ||
              (data as any).overallFeedback ||
              (data as any).feedbackSummary ||
              (data as any).evaluation?.overallFeedback ||
              (data as any).evaluation?.feedbackSummary
            }
            topStrength={
              data.topStrength ||
              (typeof data.coaching?.strengths?.[0] === 'object' ? (data.coaching?.strengths?.[0] as any)?.title : data.coaching?.strengths?.[0]) ||
              (criteria.find((c: any) => (c.score || 0) >= 3.5)?.name
                ? `Effective ${criteria.find((c: any) => (c.score || 0) >= 3.5)?.name}`
                : undefined)
            }
            topStrengthScore={
              (data as any).topStrengthScore ||
              (criteria.find((c: any) => (c.score || 0) >= 3.5)?.score
                ? `${criteria.find((c: any) => (c.score || 0) >= 3.5)?.score}/5`
                : undefined)
            }
            biggestGap={
              data.biggestGap ||
              (typeof data.coaching?.improvements?.[0] === 'object' ? (data.coaching?.improvements?.[0] as any)?.title : data.coaching?.improvements?.[0]) ||
              (typeof data.coaching?.scopeForImprovement?.[0] === 'object' ? (data.coaching?.scopeForImprovement?.[0] as any)?.title : data.coaching?.scopeForImprovement?.[0]) ||
              (criteria.find((c: any) => c.score !== null && (c.score || 0) < 3.5)?.name
                ? `Focus on ${criteria.find((c: any) => c.score !== null && (c.score || 0) < 3.5)?.name}`
                : undefined)
            }
            biggestGapScore={
              (data as any).biggestGapScore ||
              (criteria.find((c: any) => c.score !== null && (c.score || 0) < 3.5)?.score
                ? `${criteria.find((c: any) => c.score !== null && (c.score || 0) < 3.5)?.score}/5`
                : undefined)
            }
            deliverySummary={
              data.deliverySummary ||
              (data.communicationMetrics?.pace?.wpm
                ? data.communicationMetrics.pace.wpm > 160
                  ? 'Fast speaking pace'
                  : data.communicationMetrics.pace.wpm < 110
                    ? 'Slow speaking pace'
                    : 'Balanced speaking pace'
                : undefined)
            }
            deliveryMetric={
              data.deliveryWpm
                ? `${data.deliveryWpm} wpm`
                : data.communicationMetrics?.speakingPace?.wpm
                  ? `${data.communicationMetrics.speakingPace.wpm} wpm`
                  : data.communicationMetrics?.pace?.wpm
                    ? `${data.communicationMetrics.pace.wpm} wpm`
                    : (data as any).evaluation?.communicationMetrics?.speakingPace?.wpm
                      ? `${(data as any).evaluation.communicationMetrics.speakingPace.wpm} wpm`
                      : undefined
            }
          />

          {/* Card 2: Competency Breakdown */}
          <CompetencyBreakdownCard
            criteria={criteria}
            scoringTraceability={data.scoringTraceability || (data as any).evaluation?.scoringTraceability}
            showTraceability={false}
          />

          {/* Card 3: Communication & Delivery */}
          <CommunicationDeliveryGrid
            metrics={data.communicationMetrics || (data as any).evaluation?.communicationMetrics}
            transcript={transcript}
          />

          {/* Card 4: Video Observations / Body Language */}
          <VideoObservationsCard
            evidence={data.videoEvidence || (data as any).evaluation?.videoEvidence || data.bodyLanguage || (data as any).evaluation?.bodyLanguage}
            bodyLanguage={data.bodyLanguage || (data as any).evaluation?.bodyLanguage || data.videoEvidence || (data as any).evaluation?.videoEvidence}
            requiresCamera={requiresCamera}
            isAudioOnly={isAudioOnly}
            videoAnalysisEnabled={videoAnalysisEnabled}
          />

          {/* Card 5: Conversation Quality */}
          <ConversationQualityCard
            requiredSteps={requiredSteps}
            timeTaken={timeTaken || undefined}
            sentimentTimeline={
              data.sentimentTimeline ||
              (data as any).conversationQuality?.sentimentTimeline ||
              (data as any).evaluation?.sentimentTimeline ||
              (data as any).evaluation?.conversationQuality?.sentimentTimeline
            }
          />

          {/* Card 6: Replay & Key Moments */}
          <ReplayKeyMomentsCard
            moments={data.replays || (data as any).keyMoments || (data as any).evaluation?.keyMoments || []}
            personaName={cleanPersonaName || 'Customer Persona'}
            learnerName={learnerName}
            duration={timeTaken || undefined}
            videoUrl={resolvedVideoUrl}
            personaAvatarUrl={(data as any).personaAvatarUrl || (data as any).scenario?.persona?.avatarUrl || (data as any).scenario?.avatarUrl}
            personaAvatarType={(data as any).personaAvatarType || (data as any).avatarType || (data as any).scenario?.persona?.avatarType || (data as any).scenario?.avatarType}
            personaRole={(data as any).personaRole || (data as any).scenario?.persona?.role || (data as any).scenario?.role || (data as any).scenario?.persona?.title}
            personaMood={(data as any).personaMood || (data as any).scenario?.persona?.mood || (data as any).scenario?.mood || (data as any).scenario?.persona?.tone || (data as any).scenario?.tone}
            genderPreference={resolvedGenderPreference}
            ttsVoice={resolvedTtsVoice}
            ttsProvider={resolvedTtsProvider}
            attemptId={data.attemptId}
            country={(data as any).country || (data as any).scenario?.persona?.country}
            tone={(data as any).tone || (data as any).scenario?.persona?.tone}
            transcript={transcript}
            onRetakeSimulation={handleRetrySimulation}
            simulationTitle={data.simulationTitle || undefined}
            category={data.category || (data as any).scenario?.category || undefined}
            language={language || undefined}
          />
        </div>

        {/* RIGHT COLUMN */}
        <div style={{ display: 'grid', gap: '20px', minWidth: 0, width: '100%', maxWidth: '100%' }}>
          {/* Card 1: Improvement Tips */}
          <ImprovementTipsCard
            tips={
              (data.coaching?.improvements as any) ||
              (data.coaching?.scopeForImprovement as any) ||
              (data.coaching?.actionableTips as any) ||
              (data as any).improvements ||
              (data as any).areasForImprovement ||
              []
            }
          />

          {/* Card 2: Coach Comments */}
          <CoachCommentsCard
            resetKey={data.attemptId}
            comments={data.coachComments}
            feedbackSummary={
              data.coaching?.overallFeedback ||
              data.coaching?.summary ||
              (data as any).overallFeedback ||
              (data as any).feedbackSummary ||
              (data as any).evaluation?.overallFeedback ||
              (data as any).evaluation?.feedbackSummary
            }
            evaluatedBy={evaluatedBy}
            learnerName={learnerName}
          />

          {/* Assessment Session Recording (Placed directly above Conversation Transcript) */}
          {data.attemptId && (
            <AssessmentRecordingReplayCard
              attemptId={data.attemptId}
              videoUrl={resolvedVideoUrl}
              duration={timeTaken || undefined}
              simulationTitle={scenarioTitle}
              learnerName={learnerName}
              recordedAt={(data as any).videoRecording?.startedAt || (data as any).startedAt || (data as any).createdAt || submittedAt || evaluatedAt || undefined}
              transcript={transcript}
              personaName={cleanPersonaName || 'AI Customer'}
              personaAvatarUrl={(data as any).personaAvatarUrl || (data as any).scenario?.persona?.avatarUrl || (data as any).scenario?.avatarUrl}
              personaAvatarType={(data as any).personaAvatarType || (data as any).avatarType || (data as any).scenario?.persona?.avatarType || (data as any).scenario?.avatarType}
              personaRole={(data as any).personaRole || (data as any).scenario?.persona?.role || (data as any).scenario?.role || (data as any).scenario?.persona?.title}
              personaMood={(data as any).personaMood || (data as any).scenario?.persona?.mood || (data as any).scenario?.mood || (data as any).scenario?.persona?.tone || (data as any).scenario?.tone}
              genderPreference={resolvedGenderPreference}
              ttsVoice={resolvedTtsVoice}
              country={(data as any).country || (data as any).scenario?.persona?.country}
              tone={(data as any).tone || (data as any).scenario?.persona?.tone}
              recordingStatus={
                (data as any).videoRecording?.status ||
                (resolvedVideoUrl ? 'READY' : (data as any).recordingStatus || ((data as any).videoRecording?.errorReason ? 'FAILED' : undefined))
              }
              captureMode={(data as any).videoRecording?.captureMode || (data as any).captureMode}
              mimeType={(data as any).videoRecording?.mimeType}
              errorReason={(data as any).videoRecording?.errorReason}
            />
          )}

          {/* Card 3: Conversation Transcript (with on-demand inline expansion & modal) */}
          <ConversationTranscriptCard
            transcript={transcript}
            onOpenModal={() => setShowTranscriptModal(true)}
            personaName={cleanPersonaName}
            learnerName={learnerName}
          />
        </div>
      </div>

      {/* Modals */}
      <TranscriptViewerModal
        isOpen={showTranscriptModal}
        onClose={() => setShowTranscriptModal(false)}
        transcript={transcript}
        scenarioTitle={scenarioTitle}
        personaName={cleanPersonaName}
      />

      <InspectPromptsModal
        isOpen={showInspectModal}
        onClose={() => setShowInspectModal(false)}
        simulationTitle={scenarioTitle}
        evalData={data}
      />
    </div>
  );
};
